// src/contexts/translation.tsx
import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useReducer,
  ReactNode,
} from "react";

// Polyfill for requestIdleCallback
if (typeof window !== "undefined") {
  if (!window.requestIdleCallback) {
    window.requestIdleCallback = function (cb, options = {}) {
      const start = Date.now();
      return window.setTimeout(() => {
        cb({
          didTimeout: false,
          timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
        });
      }, options.timeout || 1);
    };
  }

  if (!window.cancelIdleCallback) {
    window.cancelIdleCallback = function (id) {
      clearTimeout(id);
    };
  }
}

interface ProgressItem {
  file: string;
  progress: Record<string, string | number>;
}

interface TranslationContextValue {
  ready: boolean;
  progress: ProgressItem[];
  sourceLanguage: string;
  targetLanguage: string;
  setSourceLanguage: (lang: string) => void;
  setTargetLanguage: (lang: string) => void;
  enqueueTranslate: (root?: Element) => void;
  stopTranslation: () => void;
  error: string | null;
}

const TranslationContext = createContext<TranslationContextValue>(
  {} as TranslationContextValue,
);

type QueueAction =
  | { type: "enqueue"; root: Element }
  | { type: "dequeue" }
  | { type: "clear" };

interface QueueState {
  queue: Element[];
  set: Set<Element>;
}

function queueReducer(state: QueueState, action: QueueAction): QueueState {
  switch (action.type) {
    case "enqueue": {
      if (state.set.has(action.root)) return state;
      const newSet = new Set(state.set);
      newSet.add(action.root);
      return { queue: [...state.queue, action.root], set: newSet };
    }
    case "dequeue": {
      if (state.queue.length === 0) return state;
      const [first, ...rest] = state.queue;
      const updatedSet = new Set(state.set);
      updatedSet.delete(first);
      return { queue: rest, set: updatedSet };
    }
    case "clear":
      return { queue: [], set: new Set() };
    default:
      return state;
  }
}

function throttle<T extends (...args: Element[]) => void>(
  fn: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let last = 0;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - last >= wait) {
      last = now;
      fn(...args);
    }
  };
}

export const TranslationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const workerRef = useRef<Worker | null>(null);
  const initWorkerRef = useRef<() => void>(() => {});
  const processQueueRef = useRef<() => void>(() => {});

  const [queueState, dispatch] = useReducer(queueReducer, {
    queue: [] as Element[],
    set: new Set<Element>(),
  });

  const [enqueueFlag, setEnqueueFlag] = useState(0);

  const enqueueTranslate = throttle((root: Element = document.body) => {
    dispatch({ type: "enqueue", root });
    setEnqueueFlag((x) => x + 1);
  }, 100);

  useEffect(() => {
    if (queueState.queue.length > 0) {
      processQueueRef.current();
    }
  }, [enqueueFlag, queueState.queue]);

  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState<ProgressItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sourceLanguage, setSourceLanguage] = useState("eng_Latn");
  const [targetLanguage, setTargetLanguage] = useState("eng_Latn");

  const cacheRef = useRef(new Map<string, Map<string, string>>());
  const pendingIntermediate = useRef(new Map<string, string>());
  const pendingFinal = useRef(new Map<string, string>());
  const rafInt = useRef(false);
  const rafFinal = useRef(false);

  const flushIntermediate = () => {
    rafInt.current = false;
    const batch = new Map(pendingIntermediate.current);
    pendingIntermediate.current.clear();
    batch.forEach((partial, orig) => {
      document
        .querySelectorAll<HTMLElement>(
          `[data-original-text="${CSS.escape(orig)}"]`,
        )
        .forEach((el) => {
          const tn = el.firstChild;
          if (
            el.hasAttribute("data-translation-pending") &&
            tn?.nodeType === Node.TEXT_NODE &&
            tn.textContent !== partial
          ) {
            tn.textContent = partial;
          }
        });
    });
  };

  const flushFinal = () => {
    rafFinal.current = false;
    const batch = new Map(pendingFinal.current);
    pendingFinal.current.clear();
    batch.forEach((translated, orig) => {
      document
        .querySelectorAll<HTMLElement>(
          `[data-original-text="${CSS.escape(orig)}"]`,
        )
        .forEach((el) => {
          const tn = el.firstChild;
          if (
            tn?.nodeType === Node.TEXT_NODE &&
            tn.textContent !== translated
          ) {
            tn.textContent = translated;
          }
          el.removeAttribute("data-translation-pending");
        });
    });
  };

  const applyIntermediate = useCallback((orig: string, partial: string) => {
    pendingIntermediate.current.set(orig, partial);
    if (!rafInt.current) {
      rafInt.current = true;
      requestAnimationFrame(flushIntermediate);
    }
  }, []);

  const applyFinal = useCallback((orig: string, translated: string) => {
    pendingFinal.current.set(orig, translated);
    if (!rafFinal.current) {
      rafFinal.current = true;
      requestAnimationFrame(flushFinal);
    }
  }, []);

  const runTranslationBatch = useCallback(
    (root: Element) => {
      let langCache = cacheRef.current.get(targetLanguage);
      if (!langCache) {
        langCache = new Map();
        cacheRef.current.set(targetLanguage, langCache);
      }

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          const txt = node.textContent?.trim();
          if (!txt) return NodeFilter.FILTER_REJECT;
          const p = node.parentElement!;
          if (
            ["SCRIPT", "STYLE", "NOSCRIPT", "IFRAME", "OBJECT"].includes(
              p.tagName,
            ) ||
            ["INPUT", "TEXTAREA", "SELECT", "OPTION"].includes(p.tagName) ||
            p.closest(".no-translate, .notranslate")
          ) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });

      const elemsByText = new Map<string, HTMLElement[]>();
      let node: Node | null;
      while ((node = walker.nextNode())) {
        const el = node.parentElement as HTMLElement;
        const orig =
          el.getAttribute("data-original-text") ?? node.textContent!.trim();

        if (
          root === document.body &&
          el.hasAttribute("data-translation-pending")
        ) {
          continue;
        }

        if (langCache.has(orig)) {
          applyFinal(orig, langCache.get(orig)!);
          continue;
        }

        const arr = elemsByText.get(orig) || [];
        arr.push(el);
        elemsByText.set(orig, arr);
      }

      const toTranslate = Array.from(elemsByText.keys());
      if (toTranslate.length === 0) return;

      toTranslate.forEach((orig) => {
        elemsByText.get(orig)!.forEach((el) => {
          el.setAttribute("data-original-text", orig);
          el.setAttribute("data-translation-pending", "true");
        });
      });

      workerRef.current?.postMessage({
        status: "translate",
        texts: toTranslate,
        src_lang: sourceLanguage,
        tgt_lang: targetLanguage,
      });
    },
    [applyFinal, sourceLanguage, targetLanguage],
  );

  const processQueue = useCallback(() => {
    if (!workerRef.current) {
      initWorkerRef.current();
      return;
    }

    const visible = queueState.queue.filter((el) => el.isConnected);
    if (visible.length !== queueState.queue.length) {
      dispatch({ type: "clear" });
      visible.forEach((el) => dispatch({ type: "enqueue", root: el }));
      return;
    }

    const next = visible[0];
    if (!next) return;
    dispatch({ type: "dequeue" });
    requestIdleCallback(() => runTranslationBatch(next));
  }, [queueState.queue, runTranslationBatch]);

  processQueueRef.current = processQueue;

  initWorkerRef.current = () => {
    const w = new Worker(new URL("../../worker.js", import.meta.url), {
      type: "module",
    });

    w.onmessage = ({ data }) => {
      const {
        status,
        file,
        progress: prog,
        originalText,
        output,
        error: err,
      } = data;

      switch (status) {
        case "ready":
          setReady(true);
          processQueueRef.current();
          break;
        case "initiate":
          setProgress((p) => [...p, { file: file!, progress: {} }]);
          break;
        case "progress":
          setProgress((p) =>
            p.map((it) => (it.file === file ? { ...it, progress: prog! } : it)),
          );
          break;
        case "done":
          setProgress((p) => p.filter((it) => it.file !== file));
          break;
        case "update":
          applyIntermediate(originalText!, output!);
          break;
        case "translation_completed": {
          let langCache = cacheRef.current.get(targetLanguage);
          if (!langCache) {
            langCache = new Map();
            cacheRef.current.set(targetLanguage, langCache);
          }
          langCache.set(originalText!, output!);
          applyFinal(originalText!, output!);
          break;
        }
        case "complete":
          processQueueRef.current();
          break;
        case "error":
          setError(err || "Unknown error");
          dispatch({ type: "clear" });
          setProgress([]);
          document
            .querySelectorAll<HTMLElement>(
              "[data-original-text],[data-translation-pending]",
            )
            .forEach((el) => {
              const orig = el.getAttribute("data-original-text");
              const tn = el.firstChild;
              if (orig && tn?.nodeType === Node.TEXT_NODE) {
                tn.textContent = orig;
              }
              el.removeAttribute("data-original-text");
              el.removeAttribute("data-translation-pending");
            });
          break;
      }
    };

    workerRef.current = w;
  };

  const stopTranslation = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }

    dispatch({ type: "clear" });
    setProgress([]);
    setError(null);
    setReady(false);

    document
      .querySelectorAll<HTMLElement>(
        "[data-original-text],[data-translation-pending]",
      )
      .forEach((el) => {
        const orig = el.getAttribute("data-original-text");
        const tn = el.firstChild;
        if (orig && tn?.nodeType === Node.TEXT_NODE) {
          tn.textContent = orig;
        }
        el.removeAttribute("data-original-text");
        el.removeAttribute("data-translation-pending");
      });

    initWorkerRef.current();
  }, []);

  useEffect(() => {
    initWorkerRef.current();
    return () => {
      workerRef.current?.terminate();
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "childList") {
          m.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE)
              enqueueTranslate(node as Element);
          });
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [ready, enqueueTranslate]);

  const ctx = useMemo(
    () => ({
      ready,
      progress,
      sourceLanguage,
      targetLanguage,
      setSourceLanguage,
      setTargetLanguage,
      enqueueTranslate,
      stopTranslation,
      error,
    }),
    [
      ready,
      progress,
      sourceLanguage,
      targetLanguage,
      enqueueTranslate,
      stopTranslation,
      error,
    ],
  );

  return (
    <TranslationContext.Provider value={ctx}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationContext;
